/**
 * **故障（glitch）：色块错位 + 画面撕裂。**
 *
 * 评审：
 *
 * > 写个 glitch 滤镜，表现出轻度的色块错位，画面撕裂等现象，**不改变颜色**；
 * > 濒死期间每隔 0.5-3 秒的随机时间生效 0.1-0.5 秒。
 *
 * ## 为什么"不改变颜色"是个实现约束，不是一个形容词
 *
 * 最常见的 glitch 做法是**通道错位**（RGB split：红和蓝各偏几个像素）—— 那
 * 一眼就能看出是故障，但它改的正是颜色。这里要的是"画面被**搬**了几块"：
 * 只动**采样位置**，一个像素上出现的还是原来那个像素的颜色。
 *
 * ```
 *   uv' = uv + 偏移(块编号, 随机种子)     ← 只有它
 *   color = texture(原图, uv')            ← 颜色原样搬过来
 * ```
 *
 * ## 三样东西叠出来
 *
 * | 现象 | 怎么来 |
 * | --- | --- |
 * | **色块错位** | 把画面切成 `uBlock` 大小的格子，**一部分**格子整体横move几个像素（每一格自己一个随机方向和距离） |
 * | **画面撕裂** | 若干**扫描行**（一行一条带）横贯整屏、比色块搬得**更远** —— 电视上那种"半帧没对上" |
 * | **持续感** | 生效期间 `uSeed` 每帧都换：图案每帧都不一样，而不是一块静止的错位 |
 *
 * ⚠️ 采样越界时**保持原样**（`return texture(uTexture, vTextureCoord)`），不要
 * 让硬件去 clamp —— clamp 会把边缘那一列拉成一条长条，看着像贴图坏了。
 *
 * ## 时间轴：什么时候生效
 *
 * 评审给的两个区间是**空闲** 0.5–3 秒、**生效** 0.1–0.5 秒。那是纯算术，
 * 放在 `nextGlitchWindow()` 里（有测试）；这里只管画。
 *
 * ⚠️ 生效强度是**硬开关**（0 或 1），不做淡入淡出：故障本来就该是"啪"地一下。
 *    真正让画面不呆板的是"每帧换图案"，不是渐强。
 */

import { Filter, UniformGroup } from 'pixi.js';
import { quadProgram } from '../filterQuad';

/**
 * ## 观感参考与许可
 *
 * 色块错位 + 画面撕裂的观感参考 Shadertoy 作品 **"XtyXzW"**
 * （<https://www.shadertoy.com/view/XtyXzW>），该作品以 **CC BY-NC-SA 3.0**
 * 授权。这里没有复制其源码，而是按同样思路独立实现的着色器；按其许可给出
 * 出处署名，本项目对该效果的使用需符合「非商业 + 相同方式共享」。
 *
 * 它现在有两处用途：濒死期间挂在整块画面上的常态故障，以及出牌结算后
 * **原地故障消失**（render/game/playReveal.ts）。
 */

/** 一次"生效"和一次"空闲"有多久（毫秒） */
export interface GlitchWindow {
  readonly idleMs: number;
  readonly activeMs: number;
}

/** 评审给的区间（毫秒） */
export const GLITCH_IDLE_MS = { min: 500, max: 3000 } as const;
export const GLITCH_ACTIVE_MS = { min: 100, max: 500 } as const;

/**
 * **常态强度**（0..1）。评审：
 *
 * > glitch 效果我希望改为常态存在，但强度极为轻微。
 *
 * ⚠️ "常态"指的是**濒死期间**常态（评审后来的澄清：「我说的常态存在是指濒死
 *    期间常态存在，而不是整个游戏运行期间常态存在」）—— 所以挂/摘滤镜仍然
 *    由 `isDying` 决定，只是**在濒死那段时间里它一直是开的**，
 *    不再"闪一下又关掉"。
 *
 * 强度现在**真的**乘在位移上（见片元着色器），所以这个数就是"画面被搬动多少"：
 * `0.14 × 7px ≈ 1px` 的色块错位 —— 安静的时候几乎看不出，只看得出"画面不是
 * 完全静止的"。
 */
export const GLITCH_BASE_STRENGTH = 0.14;
/**
 * **濒死出现的那一瞬间，强度先 x10，再在 1 秒内指数衰减。**
 *
 * 评审：
 *
 * > glitch 随濒死特效出现的瞬间强度为 10 倍，在 1 秒内指数衰减。
 *
 * 指数的时间常数取 0.25 秒：`exp(-1/0.25) = 1.8%` —— 到 1 秒时那 10 倍只剩
 * 不到两成，正好是"啪地一下抽过去、一秒内平静下来"。
 */
export const GLITCH_ONSET_MULTIPLIER = 10;
export const GLITCH_ONSET_TAU_S = 0.25;

/**
 * **临近濒死那一刻的强度倍率**（从 10 指数衰减回 1）。
 *
 * 纯函数，`elapsedMs` 是"从濒死开始到现在过了多久"。
 */
export function glitchOnsetBoost(elapsedMs: number): number {
  const t = Math.max(0, elapsedMs) / 1000;
  return 1 + (GLITCH_ONSET_MULTIPLIER - 1) * Math.exp(-t / GLITCH_ONSET_TAU_S);
}

/**
 * **爆发倍率。** 评审：「原有的"每隔一段时间触发"，改为"每隔一段时间强度 x3"」。
 *
 * 原来那套是"0.1–0.5 秒整段生效"（硬开关），现在常态就有很轻的一层，
 * 每隔一段时间把强度乘上去 —— 信号抽一下，而不是"故障开了一会儿"。
 */
export const GLITCH_BURST_MULTIPLIER = 3;
/**
 * 强度随机漫步的范围（相对基准强度的倍率）。
 *
 * 评审：「强度数值每帧进行随机漫步」。上下各 50% 就够了 —— 再大就会
 * 明显看出"忽轻忽重"，而那是另一种观感（呼吸），不是抖动。
 */
export const GLITCH_WALK_RANGE = { min: 0.5, max: 1.5 } as const;
/** 每帧最多挪多少（相对基准强度） */
export const GLITCH_WALK_STEP = 0.08;

/** 色块大小（屏幕**物理**像素）。太小像噪声，太大看不出是"块" */
export const GLITCH_BLOCK_PX = 26;
/** 色块最多搬多远（屏幕物理像素，**满强度**时）。评审要"轻度"，所以是个位数 */
export const GLITCH_BLOCK_SHIFT_PX = 7;
/**
 * **带状撕裂搬多远**（屏幕**物理**像素）。
 *
 * ★ **没有"条带高度"这个参数了。** 评审：
 *
 * > 故障滤镜在部分设备上会导致屏幕分辨率下降，请使片段着色器不再去计算
 * > "条带高度"，片段着色器获得的每个不同垂直坐标都视为一个不同条带。
 *
 * 原来那条路是 `floor(px.y / 高度)` —— 它把"有多少条带"交给一个除法去算，
 * 而 `px` 当时是**拿 uv 当屏幕坐标**乘出来的（uv 只覆盖输入贴图的一小块，
 * 见下面 `uGlitchA` 的说明），于是在 DPR≠1 的机器上 1 像素的条带实际有
 * 两三个像素高：读取来就是"屏幕分辨率掉了"。
 *
 * 现在条带的身份**就是那个垂直坐标本身**：一行一个哈希值。一行有多细不由
 * 这里决定 —— 它由屏幕自己的像素栅格决定，这正是"分辨率"该由谁决定。
 */
export const GLITCH_TEAR_SHIFT_PX = 26;

/**
 * **下一次故障在多久之后、持续多久。** 纯函数（吃一个 0..1 的随机数发生器）。
 *
 * 区间来自评审的原话：「每隔 0.5-3 秒的随机时间生效 0.1-0.5 秒」。
 */
export function nextGlitchWindow(random: () => number): GlitchWindow {
  const span = (min: number, max: number): number => min + (random() ** 1.5) * (max - min);
  return {
    idleMs: span(GLITCH_IDLE_MS.min, GLITCH_IDLE_MS.max),
    activeMs: span(GLITCH_ACTIVE_MS.min, GLITCH_ACTIVE_MS.max),
  };
}

/**
 * **故障的时间表** —— 现在只回答一个问题："这一帧是不是爆发期"。
 *
 * 用法：**只在濒死期间**每帧 `advance(deltaMS)`，返回 `true`（这一轮是爆发）
 * 或 `false`（安静）。强度由调用方算（常态 + 随机漫步 + 爆发倍率，
 * 见 `glitchStrength`）。
 *
 * ⚠️ 它**不再管"开不开"**：评审要的"常态存在"是**濒死期间**常态存在
 *    （不是整个游戏运行期间）—— 挂/摘滤镜仍然由调用方按 `isDying` 决定，
 *    它只管"什么时候把强度乘 3"。
 *
 * ⚠️ 随机数走 `core/rng` 的种子化 PRNG（和裂缝、抽牌堆同一套纪律）：给同一个
 *    种子必然得到同一串窗口，于是"这个观感对不对"是可复现、可断言的。
 */
export interface GlitchClock {
  /** 推一帧。返回这一帧是不是**爆发期** */
  advance(deltaMS: number): boolean;
  /** 现在在爆发期吗（读数用） */
  readonly bursting: boolean;
  /** 这一轮还剩多久（诊断用） */
  readonly phaseLeftMs: number;
  /**
   * **回到"刚开局"**：下一次 `advance` 会从一整段全新的空闲期开始。
   *
   * ⚠️ 濒死**结束**时调它 —— 不然下一次抽到致命牌会接着上一轮的进度
   *    （可能正好撞在爆发期的尾巴上），第一次抖动就不是"从安静开始"了。
   */
  reset(): void;
}

export function createGlitchClock(random: () => number = Math.random): GlitchClock {

  let window: GlitchWindow | null = null;
  let leftMs = 0;
  let active = false;

  const roll = (): void => {
    window = nextGlitchWindow(random);
    active = false;
    leftMs = window.idleMs;
  };

  return {
    advance(deltaMS) {
      if (window === null) roll();
      leftMs -= deltaMS;
      // ⚠️ `while` 而不是 `if`：一帧可能比一整段还长（软件渲染、切回标签页），
      //    那时要顺着往后翻几段，而不是"下一帧再说"
      while (leftMs <= 0 && window !== null) {
        if (active) {
          active = false;
          leftMs += window.idleMs;
        } else {
          active = true;
          leftMs += window.activeMs;
        }
      }
      return active;
    },
    reset() {
      window = null;
      active = false;
      leftMs = 0;
    },
    get bursting() {
      return active;
    },
    get phaseLeftMs() {
      return leftMs;
    },
  };
}

// ---------------------------------------------------------------- 滤镜 ----

export const GLITCH_FRAGMENT = /* glsl */ `
in vec2 vTextureCoord;
out vec4 finalColor;

uniform sampler2D uTexture;
/**
 * **(色块大小, 块位移上限, 输入贴图宽, 输入贴图高)** —— 前两项是**物理像素**。
 *
 * ⚠️ 后面那两项由 'GlitchFilter.apply()' 从 Pixi 手里**现读**（见那个方法）：
 *    滤镜的输入贴图是从纹理池里拿的，池子给的贴图**可能比被渲染的区域大一圈**
 *    （1280×800 的区域常常拿到 2048×1024 的贴图），于是
 *    'vTextureCoord' 只覆盖 '[0, 0.625] × [0, 0.78]' 这样一小块 ——
 *    任何"拿 uv 当屏幕坐标"的算法都会整体错一个倍率，而且**倍率随设备变**
 *    （DPR 1 的机器上池子刚好给一样大，所以只有部分设备出问题：撕裂的条带
 *    在那些机器上会粗两三倍，读起来就是"屏幕分辨率掉了"）。
 *
 *    贴图的**物理**尺寸（'source.pixelWidth'）乘回 uv 恰好是"区域里的物理像素
 *    坐标"：'uv = 四边形坐标 × frame / source宽'，乘上 'source宽 × 分辨率'
 *    就是 '四边形坐标 × frame × 分辨率' = 区域里的物理像素数。
 *
 * ⚠️ 屏幕尺寸**不在着色器里问**：'textureSize()' 是 GLSL ES 3.00 才有的，而我们
 *    这些着色器编译成 **ES 1.00**（Pixi 的 'addProgramDefines' 把 'in' 换成
 *    'varying'、'texture' 换成 'texture2D'），用了它整个程序直接编译不过 ——
 *    表现是**整屏纯黑**。踩过一次。
 *
 * ⚠️ 也不许声明 'uInputSize' / 'uOutputFrame' / 'uOutputTexture' —— 那三个名字
 *    顶点着色器里已经有了，而 GLSL ES 给两个阶段的默认精度不同，同名 uniform
 *    精度不一致会**链接失败**（Pixi 只打一句 "Could not initialize shader"，
 *    然后故障滤镜整段不生效，而它只在濒死那几秒挂着，平时完全看不出来）。
 */
uniform vec4 uGlitchA;
/**
 * **(撕裂位移上限, 强度, 时间种子, 备用)** —— 第一项是**物理像素**。
 *
 * 'y' 是**这一帧真的乘在位移上的强度**（0 就整条短路）；'z' 每帧换一个种子，
 * 图案每帧都不一样才有"信号在抽搐"的观感。
 */
uniform vec4 uGlitchB;
/**
 * **(色差像素, 去色, 反相, 备用)** —— 濒死"打击"那一刻的颜色通道。
 *
 * 评审为这一档专门放了行：「颜色改变只要足够轻微不影响画面可读性就能接受」。
 * 平时三项都是 0，于是这一趟和以前逐位相同（连多一次采样都不会有）。
 *
 * ⚠️ 它们**塞进这一趟 pass**，不新挂滤镜：world 上已经有一层，嵌套滤镜在
 *    软件渲染下很贵，而且刚炸过一次（见 'render/filterStack.ts'）。
 */
uniform vec4 uGlitchC;

float hash21(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

/**
 * **颜色通道**：去色（保住"本来就是红的那片"）、色差、反相。
 *
 * 去色为什么要把红加回来：这一刀是给濒死用的，而那个画面里**唯一不能失去的
 * 信息就是异常牌自己的红**。整幅去色会把红光一起抽干，于是"威胁"没了。
 * 做法是廉价的选择性去色：色度里偏红的分量（'r - max(g, b)'）乘回去。
 */
vec3 impactColor(vec3 c) {
  float desat = uGlitchC.y;
  float invert = uGlitchC.z;
  float aberr = uGlitchC.x;
  if (desat > 0.0) {
    float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
    float redish = max(0.0, c.r - max(c.g, c.b));
    vec3 faded = mix(c, vec3(lum), desat);
    faded.r += redish * desat * 1.8;
    c = faded;
  }
  if (invert > 0.0) c = mix(c, vec3(1.0) - c, clamp(invert, 0.0, 1.0));
  return c;
}

void main() {
  vec2 uv = vTextureCoord;
  float strength = uGlitchB.y;
  float aberrPx = uGlitchC.x;
  float colorOn = max(uGlitchC.y, uGlitchC.z);
  /**
   * ⚠️ 短路条件里**必须带上颜色那两项**：常态（强度 ~0.14）之外还有"强度为 0
   *    但颜色还开着"的帧（打击刚过去那几十毫秒），先前的短路会把它们吃掉。
   */
  if (strength <= 0.0005 && colorOn <= 0.0005) {
    finalColor = texture(uTexture, uv);
    return;
  }

  /**
   * **这一片元在被渲染区域里的物理像素坐标。**
   *
   * ⚠️ 它是下面所有图案（色块、撕裂、位移）唯一的空间基准。用 uv 直接乘
   *    屏幕尺寸是**错的**（见 uGlitchA 的说明），而且错得随设备变。
   */
  vec2 texels = max(uGlitchA.zw, vec2(1.0));
  vec2 px = uv * texels;

  /**
   * ---- ① 色块错位：一部分格子整体横移 ----
   *
   * 0.72 是"有多少比例的块会被搬"的门槛：约三成的块动。门槛越低越糊、
   * 越高越像没事发生（评审要"轻度"）。
   */
  vec2 block = floor(px / max(uGlitchA.x, 1.0));
  float pick = hash21(block + vec2(uGlitchB.z * 0.017, uGlitchB.z * 0.031));
  if (pick > 0.72) {
    float dir = hash21(block * 1.37 + vec2(uGlitchB.z * 0.11, 7.0));
    uv.x += (dir - 0.5) * 2.0 * uGlitchA.y * strength / texels.x;
  }

  /**
   * ---- ② 画面撕裂：整屏宽的窄带搬得更远 ----
   *
   * ★ **一行一条带，没有"高度"这个中间量**（评审）：条带的身份就是
   *   px.y 这个垂直坐标本身。0.94 的门槛 = 约 6% 的扫描行被撕开，
   *   比色块搬得远得多，读起来才是"信号断了"而不是"多了几个方块"。
   */
  float tear = hash21(vec2(px.y * 3.7, uGlitchB.z * 0.23));
  if (tear > 0.94) {
    uv.x += (tear - 0.5) * 2.0 * uGlitchB.x * strength / texels.x;
  }

  /**
   * ⚠️ 越界就**保持原样**：让硬件 clamp 会把边缘那一列拉成一条长条，
   *    看着像贴图坏了，而不像"画面被搬了一块"。
   */
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    finalColor = texture(uTexture, vTextureCoord);
    return;
  }

  vec4 sampled = texture(uTexture, uv);
  vec3 c = sampled.rgb;
  if (aberrPx > 0.0) {
    // ---- B2 色差：红往一边、蓝往另一边，绿不动 ----
    vec2 d = vec2(aberrPx / texels.x, 0.0);
    vec2 uvR = clamp(uv + d, vec2(0.0), vec2(1.0));
    vec2 uvB = clamp(uv - d, vec2(0.0), vec2(1.0));
    c = vec3(texture(uTexture, uvR).r, c.g, texture(uTexture, uvB).b);
  }
  finalColor = vec4(impactColor(c), sampled.a);
}
`;


/**
 * **这一帧的强度。**
 *
 * ```
 *   强度 = 基准 × 漫步值 × (爆发期 ? 3 : 1)
 * ```
 *
 * 纯函数，所以"极为轻微""每帧漫步""爆发乘 3"三件事都能在 node 里断言。
 * 夹在 [0,1] 里：位移是按强度乘的，超过 1 就不是"轻度错位"了。
 */
export function glitchStrength(
  walkValue: number,
  bursting: boolean,
  /** 从濒死开始过了多久（毫秒）。见 `glitchOnsetBoost` */
  onsetElapsedMs = Number.POSITIVE_INFINITY,
  base = GLITCH_BASE_STRENGTH,
): number {
  const boosted =
    base *
    walkValue *
    (bursting ? GLITCH_BURST_MULTIPLIER : 1) *
    glitchOnsetBoost(onsetElapsedMs);
  return Math.max(0, Math.min(1, boosted));
}

export class GlitchFilter extends Filter {
  readonly glitchUniforms: UniformGroup;
  #seed = 1;

  constructor() {
    const uniforms = new UniformGroup({
      // z/w（输入贴图的物理尺寸）由 `apply()` 每帧现读，这里先给 1
      uGlitchA: { value: new Float32Array([GLITCH_BLOCK_PX, GLITCH_BLOCK_SHIFT_PX, 1, 1]), type: 'vec4<f32>' },
      uGlitchB: { value: new Float32Array([GLITCH_TEAR_SHIFT_PX, 0, 1, 0]), type: 'vec4<f32>' },
      // (色差像素, 去色, 反相, 备用) —— 濒死"打击"那一瞬间的颜色通道
      uGlitchC: { value: new Float32Array([0, 0, 0, 0]), type: 'vec4<f32>' },
    });
    super({
      glProgram: quadProgram(GLITCH_FRAGMENT, 'glitch'),
      resources: { glitchUniforms: uniforms },
      /**
       * ★ **中间贴图按渲染器的分辨率申请。**
       *
       * ⚠️ Pixi 的 `Filter.resolution` 默认是 **1**（一个逻辑单位一个纹素）。
       *    这个滤镜挂在 `world` 上（整块画面），于是那趟中间渲染的密度和屏幕
       *    对不上：实测 DPR 2 的屏幕上，**同一个背景**不挂滤镜时中间灰
       *    1.01%，挂上（强度 0.15）就到 3.78% —— 整个画面被重新采样了一遍，
       *    读起来就是"屏幕分辨率下降"；1 物理像素高的撕裂条带在那张图上
       *    也根本表示不出来。
       *
       *    `'inherit'` = 跟渲染器走（DPR 2 就是 2）。改完实测：不挂 1.01%、
       *    挂着 1.11% —— 画面只多了"被搬走的那几条"。
       *
       * ⚠️ 它和"图案量的是物理像素"是**同一件事的两半**（另一半见 `uGlitchA`）：
       *    这一半保证贴图够密，那一半保证坐标对得上。少任何一半，撕裂都会粗。
       */
      resolution: 'inherit',
    });
    this.glitchUniforms = uniforms;
  }

  /**
   * 摆这一帧的样子。`strength` 0 就整条短路（着色器里也不采样第二次）。
   *
   * ⚠️ **不需要屏幕尺寸**了：输入贴图的物理尺寸只有 Pixi 知道，而它在
   *    `apply()` 里现成地递给我们（见那个方法）。调用方以前传的那个"屏幕尺寸"
   *    是**逻辑**坐标，和 uv 根本不是一个坐标系 —— 那个错让撕裂在 DPR≠1 的
   *    机器上粗两三倍。
   *
   * ⚠️ 每帧**换种子** —— 图案每帧都不一样，才有"信号在抽搐"的观感；
   *    种子不变的话那是一块静止的错位，像贴图错了。
   */
  setFrame(strength: number): void {
    this.#seed = (this.#seed * 1664525 + 1013904223) >>> 0;
    const b = this.glitchUniforms.uniforms.uGlitchB as Float32Array;
    b[1] = strength;
    b[2] = (this.#seed % 100000) / 1000;
  }

  /** 现在生效强度多少（读数用） */
  get strength(): number {
    return (this.glitchUniforms.uniforms.uGlitchB as Float32Array)[1] ?? 0;
  }

  /**
   * **颜色那三项**（去色 / 色差 / 反相）。见 `GLITCH_FRAGMENT` 里的 `uGlitchC`。
   *
   * ⚠️ 三项都是 0 时着色器会短路（连第二次采样都不做），所以"平时"这一趟
   *    和没有这个滤镜时**逐位相同** —— 这就是它敢塞进现有 pass 的原因。
   *
   * @param aberr 色差偏移（**物理像素**）
   * @param desat 去色（0..1，偏红的地方会被加回来）
   * @param invert 反相（0..1）
   */
  setColors(aberr: number, desat: number, invert: number): void {
    const c = this.glitchUniforms.uniforms.uGlitchC as Float32Array;
    c[0] = Math.max(0, aberr);
    c[1] = Math.max(0, Math.min(1, desat));
    c[2] = Math.max(0, Math.min(1, invert));
  }

  /** 颜色三项现在是多少（读数用） */
  get colors(): { readonly aberr: number; readonly desat: number; readonly invert: number } {
    const c = this.glitchUniforms.uniforms.uGlitchC as Float32Array;
    return { aberr: c[0] ?? 0, desat: c[1] ?? 0, invert: c[2] ?? 0 };
  }

  /**
   * **每帧真正画之前，把输入贴图的物理尺寸写进 uniform。**
   *
   * 为什么只能在这里读：那张贴图是 Pixi 的**纹理池**按区域大小现挑的，池子
   * 会给它套一个 2 的幂（或屏幕尺寸）的外壳，所以"这张贴图有多大"是个只有
   * 滤镜系统知道的运行时事实 —— 调用方猜不到，着色器也问不了（`textureSize`
   * 是 ES 3.00 的，我们的着色器编译成 ES 1.00）。
   *
   * ⚠️ 覆写的是 `apply`（管线方法），不是 `setValues` 那一类参数入口 ——
   *    Pixi 每个滤镜实例、每个 pass 都会调它一次，写在里面的 uniform 一定
   *    赶在这次 draw 之前生效。
   */
  override apply(
    filterManager: Parameters<Filter['apply']>[0],
    input: Parameters<Filter['apply']>[1],
    output: Parameters<Filter['apply']>[2],
    clearMode: boolean,
  ): void {
    const a = this.glitchUniforms.uniforms.uGlitchA as Float32Array;
    a[2] = input.source.pixelWidth;
    a[3] = input.source.pixelHeight;
    super.apply(filterManager, input, output, clearMode);
  }
}
